import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Navigation } from '@quizzach/components';
import {
	AvActions,
	AvGrid,
	AvHead,
	AvItem,
	AvPage,
	AvPlaceholder,
	BtnTeal,
} from '@quizzach/styles';
import { RoutePath } from '@quizzach/interfaces';
import { useProfile } from '@quizzach/api-hooks';
import { IcClose } from '@quizzach/assets';
import { scrollToTop } from '@quizzach/utils';

export const ProfileAvatar = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { updateProfile } = useProfile();
	const [loading, setLoading] = useState(true);
	const [imagesLoaded, setImagesLoaded] = useState(0);
	const [initialLoadTime] = useState(Date.now());
	const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);

	const avatars = Array.from({ length: 16 }, (_, i) => ({
		id: i + 1,
		name: `avatar-${i + 1}`,
	}));

	const handleChooseAvatar = (name: string) => {
		updateProfile({ profile_picture: name });
		navigate(RoutePath.Profile);
	};

	const handleImageLoad = () => {
		setImagesLoaded((prev) => prev + 1);
	};

	useEffect(() => {
		if (imagesLoaded === avatars.length) {
			const elapsedTime = Date.now() - initialLoadTime;
			const remainingTime = 300 - elapsedTime;
			const minimumDelay = remainingTime > 0 ? remainingTime : 0;

			const timeoutId = setTimeout(() => {
				setLoading(false);
			}, minimumDelay);

			return () => clearTimeout(timeoutId);
		}
	}, [imagesLoaded, avatars.length, initialLoadTime]);

	useEffect(() => {
		scrollToTop();
	}, []);

	return (
		<AvPage data-id='profile-avatar-container'>
			<Navigation />
			<AvHead>
				<h1>{t('profile.avatar.chooseTitle')}</h1>
				<button
					type='button'
					className='x'
					onClick={() => navigate(RoutePath.Profile)}
					aria-label={t('profile.voucher.back')}
				>
					<IcClose size={17} />
				</button>
			</AvHead>
			{loading && (
				<AvGrid>
					{Array.from({ length: 16 }).map((_, index) => (
						<AvPlaceholder key={index} />
					))}
				</AvGrid>
			)}
			<AvGrid style={{ display: loading ? 'none' : 'grid' }}>
				{avatars.map((avatar, index) => (
					<AvItem
						key={index}
						type='button'
						$on={selectedAvatar === avatar.name}
						onClick={() => setSelectedAvatar(avatar.name)}
					>
						<img
							src={`/avatars/${avatar.name}.webp`}
							alt={`avatar-${index}`}
							onLoad={handleImageLoad}
						/>
					</AvItem>
				))}
			</AvGrid>
			<AvActions>
				<BtnTeal
					type='button'
					disabled={!selectedAvatar}
					onClick={() => selectedAvatar && handleChooseAvatar(selectedAvatar)}
				>
					{t('profile.settings.save')}
				</BtnTeal>
			</AvActions>
		</AvPage>
	);
};
